import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import './RegisterModal.css';

const RegisterModal = ({ isOpen, onClose, onLoginClick, onVerifyEmail }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!agreedToTerms) {
      setError('Please agree to the Terms of Use & Privacy Policy');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);

    try {
      const result = await register(email, password, confirmPassword);
      
      if (result.success) {
        // Clear form
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setAgreedToTerms(false);
        
        // Trigger email verification flow
        if (onVerifyEmail) {
          onVerifyEmail(result.user.email);
        } else {
          onClose();
        }
      } else {
        setError(result.error || 'Registration failed. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    // Handle Google sign-in logic
    console.log('Google sign-in clicked');
  };

  if (!isOpen) return null;

  return (
    <div className="register-modal-overlay" onClick={onClose}>
      <div className="register-modal" onClick={(e) => e.stopPropagation()}>
        <div className="register-modal-header">
          <h2 className="register-title">Register</h2>
        </div>

        <form className="register-form" onSubmit={handleSubmit}>
          <div className="form-fields">
            <div className="input-field">
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="input-field password-field">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password "
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="eye-button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <svg
                  width="21"
                  height="21"
                  viewBox="0 0 21 21"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4.42312 2.83989C4.36549 2.77492 4.29553 2.72204 4.21732 2.68431C4.1391 2.64658 4.05417 2.62475 3.96745 2.62009C3.88074 2.61542 3.79396 2.62802 3.71214 2.65714C3.63033 2.68626 3.55511 2.73133 3.49084 2.78973C3.42657 2.84814 3.37454 2.91872 3.33775 2.99738C3.30096 3.07605 3.28015 3.16123 3.27652 3.248C3.2729 3.33476 3.28653 3.42139 3.31663 3.50284C3.34673 3.5843 3.3927 3.65898 3.45187 3.72254L5.03015 5.45914C2.05077 7.28762 0.769445 10.1062 0.712844 10.2342C0.67553 10.3181 0.65625 10.4089 0.65625 10.5008C0.65625 10.5926 0.67553 10.6835 0.712844 10.7674C0.741555 10.8322 1.43636 12.3727 2.98101 13.9174C5.03917 15.9747 7.63874 17.0625 10.5 17.0625C11.9705 17.0708 13.4261 16.7681 14.7714 16.1741L16.576 18.16C16.6337 18.225 16.7036 18.2779 16.7818 18.3156C16.8601 18.3533 16.945 18.3752 17.0317 18.3798C17.1184 18.3845 17.2052 18.3719 17.287 18.3428C17.3688 18.3137 17.4441 18.2686 17.5083 18.2102C17.5726 18.1518 17.6246 18.0812 17.6614 18.0025C17.6982 17.9239 17.719 17.8387 17.7226 17.7519C17.7263 17.6652 17.7126 17.5785 17.6825 17.4971C17.6524 17.4156 17.6065 17.3409 17.5473 17.2774L4.42312 2.83989ZM8.30566 9.06114L11.7239 12.8223C11.2091 13.0931 10.619 13.1852 10.0462 13.0843C9.47341 12.9834 8.95034 12.6951 8.55915 12.2646C8.16796 11.8342 7.9308 11.286 7.88492 10.7062C7.83904 10.1264 7.98703 9.54773 8.30566 9.06114ZM10.5 15.75C7.97507 15.75 5.76925 14.832 3.94323 13.0224C3.19373 12.2776 2.55627 11.4279 2.05077 10.5C2.4355 9.77891 3.66351 7.76094 5.93495 6.44926L7.41151 8.06938C6.83987 8.80151 6.54546 9.71216 6.58032 10.6404C6.61518 11.5686 6.97707 12.4546 7.60202 13.1418C8.22697 13.829 9.07474 14.2731 9.99549 14.3957C10.9162 14.5182 11.8507 14.3113 12.6336 13.8116L13.8419 15.1405C12.7755 15.5496 11.6422 15.7563 10.5 15.75ZM10.9922 7.92172C10.8212 7.88909 10.6701 7.78986 10.5723 7.64587C10.4745 7.50187 10.4378 7.32491 10.4705 7.15391C10.5031 6.98291 10.6023 6.83187 10.7463 6.73403C10.8903 6.63619 11.0673 6.59956 11.2383 6.63219C12.0747 6.79434 12.8362 7.22253 13.4094 7.85289C13.9825 8.48325 14.3365 9.282 14.4186 10.13C14.4348 10.3033 14.3815 10.4759 14.2705 10.6099C14.1594 10.7439 13.9996 10.8283 13.8264 10.8445C13.8059 10.8457 13.7853 10.8457 13.7648 10.8445C13.6008 10.8452 13.4425 10.7845 13.321 10.6742C13.1995 10.564 13.1238 10.4123 13.1086 10.2489C13.0533 9.6849 12.8175 9.15373 12.4362 8.73444C12.0549 8.31515 11.5485 8.03011 10.9922 7.92172ZM20.2847 10.7674C20.2502 10.8445 19.4192 12.6845 17.5481 14.3604C17.4842 14.4194 17.4091 14.4652 17.3273 14.495C17.2455 14.5248 17.1586 14.538 17.0717 14.5339C16.9847 14.5298 16.8994 14.5084 16.8208 14.471C16.7422 14.4336 16.6718 14.381 16.6137 14.3161C16.5557 14.2512 16.5111 14.1755 16.4826 14.0932C16.4541 14.0109 16.4423 13.9238 16.4478 13.8369C16.4533 13.7501 16.476 13.6651 16.5146 13.5871C16.5533 13.5091 16.6071 13.4396 16.6728 13.3825C17.5908 12.5578 18.3619 11.5831 18.9533 10.5C18.4467 9.57119 17.8078 8.72094 17.0567 7.97586C15.2307 6.16789 13.0249 5.24996 10.5 5.24996C9.96798 5.24931 9.43682 5.29239 8.91187 5.37875C8.8265 5.39385 8.73899 5.39188 8.65438 5.37297C8.56978 5.35405 8.48976 5.31856 8.41896 5.26854C8.34815 5.21852 8.28795 5.15497 8.24185 5.08156C8.19574 5.00814 8.16464 4.92632 8.15033 4.84082C8.13603 4.75531 8.1388 4.66782 8.1585 4.5834C8.17821 4.49897 8.21444 4.41929 8.26511 4.34895C8.31578 4.27861 8.37989 4.219 8.45373 4.17358C8.52757 4.12816 8.60967 4.09781 8.6953 4.0843C9.29181 3.98581 9.89541 3.9367 10.5 3.93746C13.3612 3.93746 15.9608 5.0252 18.019 7.08336C19.5636 8.62801 20.2584 10.1694 20.2871 10.2342C20.3245 10.3181 20.3437 10.4089 20.3437 10.5008C20.3437 10.5926 20.3245 10.6835 20.2871 10.7674H20.2847Z"
                    fill="#6D6D6D"
                  />
                </svg>
              </button>
            </div>

            <div className="input-field password-field">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm Password "
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="eye-button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                <svg
                  width="21"
                  height="21"
                  viewBox="0 0 21 21"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4.42312 2.83989C4.36549 2.77492 4.29553 2.72204 4.21732 2.68431C4.1391 2.64658 4.05417 2.62475 3.96745 2.62009C3.88074 2.61542 3.79396 2.62802 3.71214 2.65714C3.63033 2.68626 3.55511 2.73133 3.49084 2.78973C3.42657 2.84814 3.37454 2.91872 3.33775 2.99738C3.30096 3.07605 3.28015 3.16123 3.27652 3.248C3.2729 3.33476 3.28653 3.42139 3.31663 3.50284C3.34673 3.5843 3.3927 3.65898 3.45187 3.72254L5.03015 5.45914C2.05077 7.28762 0.769445 10.1062 0.712844 10.2342C0.67553 10.3181 0.65625 10.4089 0.65625 10.5008C0.65625 10.5926 0.67553 10.6835 0.712844 10.7674C0.741555 10.8322 1.43636 12.3727 2.98101 13.9174C5.03917 15.9747 7.63874 17.0625 10.5 17.0625C11.9705 17.0708 13.4261 16.7681 14.7714 16.1741L16.576 18.16C16.6337 18.225 16.7036 18.2779 16.7818 18.3156C16.8601 18.3533 16.945 18.3752 17.0317 18.3798C17.1184 18.3845 17.2052 18.3719 17.287 18.3428C17.3688 18.3137 17.4441 18.2686 17.5083 18.2102C17.5726 18.1518 17.6246 18.0812 17.6614 18.0025C17.6982 17.9239 17.719 17.8387 17.7226 17.7519C17.7263 17.6652 17.7126 17.5785 17.6825 17.4971C17.6524 17.4156 17.6065 17.3409 17.5473 17.2774L4.42312 2.83989ZM8.30566 9.06114L11.7239 12.8223C11.2091 13.0931 10.619 13.1852 10.0462 13.0843C9.47341 12.9834 8.95034 12.6951 8.55915 12.2646C8.16796 11.8342 7.9308 11.286 7.88492 10.7062C7.83904 10.1264 7.98703 9.54773 8.30566 9.06114ZM10.5 15.75C7.97507 15.75 5.76925 14.832 3.94323 13.0224C3.19373 12.2776 2.55627 11.4279 2.05077 10.5C2.4355 9.77891 3.66351 7.76094 5.93495 6.44926L7.41151 8.06938C6.83987 8.80151 6.54546 9.71216 6.58032 10.6404C6.61518 11.5686 6.97707 12.4546 7.60202 13.1418C8.22697 13.829 9.07474 14.2731 9.99549 14.3957C10.9162 14.5182 11.8507 14.3113 12.6336 13.8116L13.8419 15.1405C12.7755 15.5496 11.6422 15.7563 10.5 15.75ZM10.9922 7.92172C10.8212 7.88909 10.6701 7.78986 10.5723 7.64587C10.4745 7.50187 10.4378 7.32491 10.4705 7.15391C10.5031 6.98291 10.6023 6.83187 10.7463 6.73403C10.8903 6.63619 11.0673 6.59956 11.2383 6.63219C12.0747 6.79434 12.8362 7.22253 13.4094 7.85289C13.9825 8.48325 14.3365 9.282 14.4186 10.13C14.4348 10.3033 14.3815 10.4759 14.2705 10.6099C14.1594 10.7439 13.9996 10.8283 13.8264 10.8445C13.8059 10.8457 13.7853 10.8457 13.7648 10.8445C13.6008 10.8452 13.4425 10.7845 13.321 10.6742C13.1995 10.564 13.1238 10.4123 13.1086 10.2489C13.0533 9.6849 12.8175 9.15373 12.4362 8.73444C12.0549 8.31515 11.5485 8.03011 10.9922 7.92172ZM20.2847 10.7674C20.2502 10.8445 19.4192 12.6845 17.5481 14.3604C17.4842 14.4194 17.4091 14.4652 17.3273 14.495C17.2455 14.5248 17.1586 14.538 17.0717 14.5339C16.9847 14.5298 16.8994 14.5084 16.8208 14.471C16.7422 14.4336 16.6718 14.381 16.6137 14.3161C16.5557 14.2512 16.5111 14.1755 16.4826 14.0932C16.4541 14.0109 16.4423 13.9238 16.4478 13.8369C16.4533 13.7501 16.476 13.6651 16.5146 13.5871C16.5533 13.5091 16.6071 13.4396 16.6728 13.3825C17.5908 12.5578 18.3619 11.5831 18.9533 10.5C18.4467 9.57119 17.8078 8.72094 17.0567 7.97586C15.2307 6.16789 13.0249 5.24996 10.5 5.24996C9.96798 5.24931 9.43682 5.29239 8.91187 5.37875C8.8265 5.39385 8.73899 5.39188 8.65438 5.37297C8.56978 5.35405 8.48976 5.31856 8.41896 5.26854C8.34815 5.21852 8.28795 5.15497 8.24185 5.08156C8.19574 5.00814 8.16464 4.92632 8.15033 4.84082C8.13603 4.75531 8.1388 4.66782 8.1585 4.5834C8.17821 4.49897 8.21444 4.41929 8.26511 4.34895C8.31578 4.27861 8.37989 4.219 8.45373 4.17358C8.52757 4.12816 8.60967 4.09781 8.6953 4.0843C9.29181 3.98581 9.89541 3.9367 10.5 3.93746C13.3612 3.93746 15.9608 5.0252 18.019 7.08336C19.5636 8.62801 20.2584 10.1694 20.2871 10.2342C20.3245 10.3181 20.3437 10.4089 20.3437 10.5008C20.3437 10.5926 20.3245 10.6835 20.2871 10.7674H20.2847Z"
                    fill="#6D6D6D"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div className="terms-checkbox">
            <button
              type="button"
              className="checkbox-button"
              onClick={() => setAgreedToTerms(!agreedToTerms)}
              aria-label="Agree to terms"
            >
              {agreedToTerms ? (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M15 3.75H5C4.30964 3.75 3.75 4.30964 3.75 5V15C3.75 15.6903 4.30964 16.25 5 16.25H15C15.6903 16.25 16.25 15.6903 16.25 15V5C16.25 4.30964 15.6903 3.75 15 3.75ZM5 2.5C3.61929 2.5 2.5 3.61929 2.5 5V15C2.5 16.3807 3.61929 17.5 5 17.5H15C16.3807 17.5 17.5 16.3807 17.5 15V5C17.5 3.61929 16.3807 2.5 15 2.5H5Z"
                    fill="#1F396C"
                  />
                  <path
                    d="M8.5 12.5L5.5 9.5L6.5 8.5L8.5 10.5L13.5 5.5L14.5 6.5L8.5 12.5Z"
                    fill="#1F396C"
                  />
                </svg>
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M15 3.75H5C4.30964 3.75 3.75 4.30964 3.75 5V15C3.75 15.6903 4.30964 16.25 5 16.25H15C15.6903 16.25 16.25 15.6903 16.25 15V5C16.25 4.30964 15.6903 3.75 15 3.75ZM5 2.5C3.61929 2.5 2.5 3.61929 2.5 5V15C2.5 16.3807 3.61929 17.5 5 17.5H15C16.3807 17.5 17.5 16.3807 17.5 15V5C17.5 3.61929 16.3807 2.5 15 2.5H5Z"
                    fill="#6D6D6D"
                  />
                </svg>
              )}
            </button>
            <span className="terms-text">
              By signing up, I agree with the Terms of Use & Privacy Policy
            </span>
          </div>

          {error && (
            <div className="register-error-message" style={{ color: '#e74c3c', fontSize: '14px', marginBottom: '10px', textAlign: 'center' }}>
              {error}
            </div>
          )}

          <button type="submit" className="continue-button" disabled={isLoading}>
            {isLoading ? 'Loading...' : 'Continue'}
          </button>
        </form>

        <p className="login-link">
          Already have an account?{' '}
          <span className="login-text-link" onClick={onLoginClick}>
            Log in
          </span>
        </p>

        <div className="divider">
          <span className="divider-line"></span>
          <span className="divider-text">Or</span>
          <span className="divider-line"></span>
        </div>

        <button type="button" className="google-button" onClick={handleGoogleSignIn}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18.8 10.208C18.8 9.55801 18.7417 8.93301 18.6333 8.33301H10V11.883H14.9333C14.7167 13.0247 14.0667 13.9913 13.0917 14.6413V16.9497H16.0667C17.8 15.3497 18.8 12.9997 18.8 10.208Z"
              fill="#4285F4"
            />
            <path
              d="M9.99974 19.1667C12.4747 19.1667 14.5497 18.35 16.0664 16.95L13.0914 14.6417C12.2747 15.1917 11.2331 15.525 9.99974 15.525C7.61641 15.525 5.59141 13.9167 4.86641 11.75H1.81641V14.1167C3.32474 17.1083 6.41641 19.1667 9.99974 19.1667Z"
              fill="#07783F"
            />
            <path
              d="M4.86732 11.7416C4.68398 11.1916 4.57565 10.6083 4.57565 9.99993C4.57565 9.3916 4.68398 8.80827 4.86732 8.25827V5.8916H1.81732C1.19232 7.12493 0.833984 8.5166 0.833984 9.99993C0.833984 11.4833 1.19232 12.8749 1.81732 14.1083L4.19232 12.2583L4.86732 11.7416Z"
              fill="#FBBC05"
            />
            <path
              d="M9.99974 4.48301C11.3497 4.48301 12.5497 4.94968 13.5081 5.84968L16.1331 3.22467C14.5414 1.74134 12.4747 0.833008 9.99974 0.833008C6.41641 0.833008 3.32474 2.89134 1.81641 5.89134L4.86641 8.25801C5.59141 6.09134 7.61641 4.48301 9.99974 4.48301Z"
              fill="#EA4335"
            />
          </svg>
          <span>Continue with Google</span>
        </button>
      </div>
    </div>
  );
};

export default RegisterModal;
